import { memo, useCallback } from 'react';
import { useInView } from 'react-intersection-observer';
import { useCombinedRefs } from '@toss/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useOutsideClick } from '@/hooks';
import { cn } from '@/utils/tailwind.util';
import { editingQuizIdAtom } from '../focus/editModeAtom';
import { EditModeProvider } from '../focus/useIsEditMode';
import { inViewIndexAtom } from '../focus/useQuizInView';
import { useCheckHintValid } from './EditableHint/input/useCheckHintValid';
import { useEditableQuizCardSortable } from './hook/useEditableQuizCardSortable';
import { Body } from './Body';
import { Header } from './Header';
import { HeightAnimate } from './HeightAnimate';
import { QuizCard } from './QuizCardUI';
import { useQuizControl } from './QuizControlProvider';
export const EditableQuizCard = memo(({
  id
}: {
  id: string;
}) => {
  const setEditId = useSetAtom(editingQuizIdAtom);
  const editingId = useAtomValue(editingQuizIdAtom);
  const {
    index
  } = useQuizControl();
  const isEditMode = editingId === id;
  const {
    setNodeRef,
    attributes,
    listeners,
    style,
    isDragging
  } = useEditableQuizCardSortable({
    id
  });
  const setInViewIndex = useSetAtom(inViewIndexAtom);
  const {
    ref: inViewRef
  } = useInView({
    threshold: 0,
    rootMargin: '-45% 0% -45% 0%',
    onChange: inView => {
      if (inView) {
        setInViewIndex(index);
      }
    }
  });
  const checkHintValid = useCheckHintValid();
  const handleOutsideClick = useCallback(() => {
    if (!isEditMode) return;
    checkHintValid();
    setEditId(null);
  }, [checkHintValid, isEditMode, setEditId]);

  // 편집 중인 카드만 document mousedown 리스너를 활성화한다.
  // (1) 비편집 카드 N-1개의 contains() 검사 비용 제거
  // (2) settings step에서 quiz 트리가 display:none으로 유지되는 동안에도
  //     비활성 카드들의 전역 리스너가 살아남는 문제(이전: N개 → 이후: 최대 1개) 해소
  const outsideClickRef = useOutsideClick(() => {
    handleOutsideClick();
  }, isEditMode);
  const combinedRef = useCombinedRefs<HTMLDivElement>(setNodeRef, outsideClickRef, inViewRef);
  return <HeightAnimate ref={combinedRef} {...attributes} style={style}>
      <QuizCard.Root id={id} className={cn('w-full max-w-[650px] cursor-default group/card', isDragging && 'shadow-xl shadow-key-strong', !isEditMode && 'hover:shadow-sm', isEditMode && 'shadow-xs')}>
        <Header {...listeners} className={cn('cursor-grab', isDragging && 'cursor-grabbing')} onClick={e => {
        e.stopPropagation();
      }} />
        <EditModeProvider isEditMode={isEditMode} onEditModeChange={editMode => {
        setEditId(editMode ? id : null);
      }}>
          <Body />
        </EditModeProvider>
      </QuizCard.Root>
    </HeightAnimate>;
});